import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ArrowRight, Image as ImageIcon } from "lucide-react";
import { renderAA } from "../aa";

const EASE = [0.22, 1, 0.36, 1];

// Concept 2 — Interactive Split-Stage Dock & Focus Canvas
// Left: compact dock of all capabilities. Right: spotlight of the active one.
// Only one capability's detail shows at a time, so it always fits one viewport.
export const ConceptDock = ({ core, fde, trism }) => {
  const items = [...core, fde];
  const [active, setActive] = useState(0);
  const cap = items[active];

  return (
    <div className="h-full w-full bg-[#F7F5F0] p-6 lg:p-8 flex flex-col" data-testid="concept-dock">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">The Platform</span>
          <h2 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight mt-1 leading-tight">
            What It Takes to Achieve {renderAA("Agentic Alpha")}
          </h2>
        </div>
        <div className="hidden lg:flex items-center gap-2 rounded-full border border-[#E8AA68]/70 bg-[rgba(232,170,104,0.08)] px-3 py-1.5 shrink-0">
          <ShieldCheck size={13} className="text-[#8A5730]" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A5730]">{trism.title} Active</span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Dock */}
        <div className="col-span-5 lg:col-span-4 flex flex-col gap-2 min-h-0">
          {items.map((c, i) => {
            const isFde = c.title === fde.title;
            const on = i === active;
            return (
              <button
                key={c.title}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                data-testid={`dock-item-${i}`}
                className={`flex-1 min-h-0 text-left rounded-xl px-4 py-2.5 border transition-all duration-300 flex items-center justify-between group ${
                  isFde
                    ? `bg-[#3E2818] text-[#F7F5F0] ${on ? "border-[#E8AA68]" : "border-[#3E2818]"}`
                    : on
                    ? "bg-white border-[#8A5730]/45 shadow-[0_10px_30px_-18px_rgba(62,40,24,0.5)]"
                    : "bg-white/50 border-[#3E2818]/10 hover:border-[#8A5730]/30"
                }`}
              >
                <div>
                  {isFde && (
                    <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#E8AA68]">AI Accelerator</span>
                  )}
                  <h3 className={`font-display text-[15px] lg:text-base font-medium leading-tight ${isFde ? "text-[#F7F5F0]" : "text-[#3E2818]"}`}>
                    {c.title}
                  </h3>
                </div>
                <ArrowRight
                  size={16}
                  className={`shrink-0 transition-all duration-300 ${isFde ? "text-[#E8AA68]" : "text-[#8A5730]"} ${
                    on ? "translate-x-0 opacity-100" : "-translate-x-1 opacity-0 group-hover:opacity-70"
                  }`}
                />
              </button>
            );
          })}
          <div className="rounded-xl border border-[#E8AA68]/60 bg-[rgba(232,170,104,0.08)] px-4 py-2.5 flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#8A5730] shrink-0" />
            <span className="text-[12px] font-semibold text-[#3E2818]">{trism.title}</span>
            <span className="text-[11px] text-[#3E2818]/55 ml-auto hidden lg:inline">wraps every capability</span>
          </div>
        </div>

        {/* Stage */}
        <div className="col-span-7 lg:col-span-8 rounded-2xl bg-white border border-[#3E2818]/10 p-6 lg:p-8 min-h-0 flex flex-col overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={cap.title}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="flex flex-col h-full"
            >
              <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">{cap.title}</h3>
              <p className="text-sm text-[#3E2818]/65 mt-2 max-w-xl leading-relaxed">{cap.promise}</p>

              {/* Per-capability visual placeholder */}
              <div
                className="flex-1 min-h-0 my-4 rounded-xl border border-dashed border-[#8A5730]/35 bg-[#F7F5F0]/70 flex flex-col items-center justify-center text-center px-6"
                data-testid="dock-visual-placeholder"
              >
                <div className="w-10 h-10 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3">
                  <ImageIcon size={18} strokeWidth={1.8} />
                </div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A5730]">Visual placeholder</p>
                <p className="text-[12px] text-[#3E2818]/45 mt-1.5 max-w-xs leading-snug">
                  {cap.title} — diagram, animation or product shot goes here
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                {cap.features.map((f) => (
                  <div key={f.title} className="rounded-xl bg-[#F7F5F0] p-4 border border-[#3E2818]/8">
                    <div className="w-8 h-8 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3">
                      <f.icon size={15} strokeWidth={1.9} />
                    </div>
                    <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{f.title}</p>
                    <p className="text-[11px] text-[#3E2818]/55 mt-1.5 leading-snug">{f.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
